import { useState, useEffect, useMemo, FunctionComponent } from 'react';
import { DocumentNode, OperationVariables, LazyQueryHookOptions } from '@apollo/client';

import noop from 'utils/noop';
import { Select } from 'ui/selectv2/Select';
import { BaseOption, MultiSelectProps, SelectProps } from 'ui/selectv2/types';
import debounce from 'lodash/debounce';
import { useLazyGraphql } from 'utils/hooks/useLazyGraphql';
import { PAGINATED_SELECT_V2_TEST_ID } from './constants';
import { FilterSelectSuffix } from './PaginatedSelect.styles';
import { useAvailableOptionsV2 } from './useAvailableOptions';
import { getAvailableOptions, getSelectedOptions } from './PaginatedSelectV2.utils';

export const PAGINATED_SELECT_PLACEHOLDER = {
  MULTI: 'Select Items',
  SINGLE: 'Select an Item',
  LOADING: 'Loading...',
};

/**
 * This component is a wrapper around the `Select` component and based off of the `PaginatedSelect` component.  We've simplified a lot of the
 * logic and delegated the functionality that was built out in the `PaginatedSelect` component to the `Select` component.  This component is
 * currently not paginating, neither of these components due that so they're not really named correctly.  If we moved to a paginated query, we will
 * need to rewrite this component to support that so when we page or filter the dropdown options we are querying against the database.
 */

/**
 * DocumentNode seems to be the output type of gql`...`
 * Renaming it to GqlQuery as it feels more intuitive.
 */
type GqlQuery = DocumentNode;

export type ItemResponse = {
  /** The value of the option */
  id: string;
  /** The name/label of the option */
  name: string;
};

export type OnChangeHandler = (
  value: string,
  details: {
    option: BaseOption<string>;
  },
) => void;

export type OnMultiChangeHandler = (
  value: string[],
  details: {
    option: BaseOption<string>[];
  },
) => void;

export type PaginatedDefaultV2Props = {
  /** Callback when the value changes */
  onChange?: OnChangeHandler;
} & SelectProps;

export type PaginatedSingleV2Props = {
  /** Is this multiselect? */
  isMulti?: false;
  /** Callback when the value changes */
  onChange?: OnChangeHandler;
};

export type PaginatedMultiV2Props = {
  /** Is this multiselect? */
  isMulti: true;
  /** Callback when the value changes */
  onChange?: OnMultiChangeHandler;
};

export type ParseSingleItemV2<TItem extends ItemResponse = ItemResponse> = (
  resp: unknown,
) => TItem | undefined;
export type ParseMultipleItemsV2<TItem extends ItemResponse = ItemResponse> = (
  resp: unknown,
) => TItem[] | undefined;

export type PaginatedSelectQueryV2Props<TItem extends ItemResponse = ItemResponse> = {
  /** Query to fetch multiple items */
  fetchAllItemsQuery: GqlQuery;
  /** The initial variables to be used when fetching the items */
  fetchAllItemsVariables?: OperationVariables;
  /** Get the list of options from the `fetchItemsQuery` response */
  fetchAllItemsParser?: ParseMultipleItemsV2<TItem>;
  parseOption?: (item: TItem) => BaseOption<string>;
  fetchSelectedItemsOptions?: LazyQueryHookOptions;
};

export type BasePaginatedSelectV2Props = {
  /** Does this component contain an error */
  hasError?: boolean;
  /** Is this multiselect? */
  isMulti?: boolean;
  /** Custom trigger */
  Trigger?: FunctionComponent<React.PropsWithChildren<any>>;
  /** Current value of the component */
  value?: string;
  /* Function to set tooltip if disabled */
  setTooltipContent?: (content: string) => void;
} & Omit<SelectProps, 'options' | 'onSelect'>;

export type PaginatedSelectProps<TItem extends ItemResponse = ItemResponse> =
  BasePaginatedSelectV2Props &
    PaginatedSelectQueryV2Props<TItem> &
    (PaginatedDefaultV2Props | PaginatedSingleV2Props | PaginatedMultiV2Props);

export function parsePaginatedListItem({ name, id: optionId }: ItemResponse): BaseOption {
  return {
    label: name,
    value: `${optionId}`,
    Suffix: <FilterSelectSuffix>{optionId}</FilterSelectSuffix>,
  };
}

function identity<T = any, R = T>(value: T): R {
  return value as unknown as R;
}

function getValues(value?: string | string[]): string[] {
  if (!value) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
}

const getPlaceholder = (placeholder?: string, isLoading = false, isMulti = false) => {
  if (isLoading) return PAGINATED_SELECT_PLACEHOLDER.LOADING;
  if (placeholder) return placeholder;
  return isMulti ? PAGINATED_SELECT_PLACEHOLDER.MULTI : PAGINATED_SELECT_PLACEHOLDER.SINGLE;
};

export function PaginatedSelectV2<TItem extends ItemResponse = ItemResponse>({
  fetchAllItemsParser = identity,
  fetchAllItemsQuery,
  fetchAllItemsVariables = { search: '' },
  hasError = false,
  isMulti = false,
  parseOption = parsePaginatedListItem,
  placeholder,
  value,
  errorMsg,
  // callbacks
  onChange = noop,
  ...props
}: PaginatedSelectProps<TItem>) {
  const [searchQuery, setSearchQuery] = useState<string>();

  const values = useMemo(() => getValues(value), [value]);

  /* When the user enters a list to look up, we make a query to the server and do not use the local
     cache. This is because we want to show the user the most up to date list of items that match their
      search query and the list itself does not paginate so this gives the user the ability to look up additional
      lists.
  */
  const {
    fetch,
    data: selectedData,
    isLoading: isLoadingSelectedOptions,
    error: hasSelectGqlError,
  } = useLazyGraphql(fetchAllItemsQuery, {
    fetchPolicy: 'cache-and-network',
    ...props.fetchSelectedItemsOptions,
  });

  const selectedOptionsData = useMemo(() => {
    const parsedData = fetchAllItemsParser(selectedData);
    return parsedData ? parsedData.map(parseOption) : [];
  }, [selectedData]);

  useEffect(() => {
    fetch({ ids: values });
  }, []);

  const {
    results: availableOptionsData,
    error: hasAvailableGqlError,
    isLoading: isAvailableQueryLoading,
  } = useAvailableOptionsV2({
    query: fetchAllItemsQuery,
    variables: fetchAllItemsVariables,
    // TODO: Fix the incoming prop for this
    parseResponse: (resp: unknown) => {
      const parsedResponse = fetchAllItemsParser(resp);
      return parsedResponse ? parsedResponse.map(parseOption) : [];
    },
  });

  const searchOptionVariables = useMemo(() => {
    const baseValues = fetchAllItemsVariables ?? {};

    return {
      ...baseValues,
      search: searchQuery,
    };
  }, [searchQuery, fetchAllItemsVariables, values]);

  const { results: searchOptionsData } = useAvailableOptionsV2({
    query: fetchAllItemsQuery,
    variables: searchOptionVariables,
    skip: !searchQuery,
    // TODO: Fix the incoming prop for this
    parseResponse: (resp: unknown) => {
      const parsedResponse = fetchAllItemsParser(resp);
      return parsedResponse ? parsedResponse.map(parseOption) : [];
    },
  });

  const selectedOptions = useMemo(
    () => getSelectedOptions(selectedOptionsData, availableOptionsData, values),
    [selectedOptionsData, availableOptionsData, values],
  );

  const availableOptions = useMemo(
    () =>
      getAvailableOptions(
        searchOptionsData,
        availableOptionsData,
        selectedOptionsData,
        values,
        searchQuery,
      ),
    [selectedOptionsData, values, availableOptionsData, searchQuery],
  );

  return (
    <Select
      {...(props as SelectProps & MultiSelectProps)}
      isLoading={isAvailableQueryLoading || isLoadingSelectedOptions}
      errorMsg={
        !isAvailableQueryLoading && (hasError || hasAvailableGqlError || hasSelectGqlError)
          ? 'Error fetching items'
          : errorMsg
      }
      isMulti={isMulti}
      data-test={PAGINATED_SELECT_V2_TEST_ID}
      value={selectedOptions}
      isOptionDisabled={(option) => !!option?.isDisabled}
      onSelect={(val: BaseOption[]) => {
        if (isMulti) {
          const ids = val.map((v) => v.value);

          // if any of the ids are not in the available options, fetch them
          if (
            searchQuery &&
            ids.some((id) => !availableOptionsData.find((option) => option.value === id))
          ) {
            fetch({ ids });
          }

          (onChange as OnMultiChangeHandler)(ids, { option: val });
          if (searchQuery) {
            setSearchQuery(undefined);
          }
        } else {
          (onChange as OnChangeHandler)(val[0].value, { option: val[0] });
        }
      }}
      onInputChange={debounce((val) => setSearchQuery(val), 300)}
      options={availableOptions}
      placeholder={getPlaceholder(placeholder, isAvailableQueryLoading, isMulti)}
    />
  );
}

export default PaginatedSelectV2;
